import { ExclamationMarkOctagon } from '@/resources/assets/images';
import { forwardRef, type ComponentProps } from 'react';

import TableCell from './TableCell';
import TableRow from './TableRow';

type Props = ComponentProps<'tbody'> & {
	columns: number;
	isLoading?: boolean;
	isError?: boolean;
	isFetching?: boolean;
	paddingTop?: number;
	paddingBottom?: number;
};

const TableBody = forwardRef<HTMLTableSectionElement, Props>(
	(
		{
			columns,
			children,
			isLoading = false,
			isError = false,
			isFetching = false,
			paddingTop = 0,
			paddingBottom = 0,
			...props
		},
		ref
	) => (
		<tbody {...props} ref={ref}>
			{paddingTop > 0 && (
				<tr>
					<td colSpan={columns} style={{ height: paddingTop }} />
				</tr>
			)}

			{isLoading ? <TableLoadingRows columns={columns} /> : children}

			<TableIsFetchingRow isFetching={isFetching} columns={columns} />

			<TableErrorRow isError={isError} columns={columns} />

			{paddingBottom > 0 && (
				<tr>
					<td colSpan={columns} style={{ height: paddingBottom }} />
				</tr>
			)}
		</tbody>
	)
);

type TableErrorRowProps = {
	columns: number;
	isError: boolean;
};

const TableErrorRow = ({ isError, columns }: TableErrorRowProps) => {
	if (!isError) {
		return null;
	}

	return (
		<TableRow data-testid="table-error-row">
			<TableCell colSpan={columns}>
				<p className="text-red-500 inline-flex w-full items-center justify-center gap-2">
					<ExclamationMarkOctagon />
					Oops, something wrong happened. Please try again later.
				</p>
			</TableCell>
		</TableRow>
	);
};

type TableIsFetchingRowProps = {
	columns: number;
	isFetching: boolean;
};

const TableIsFetchingRow = ({
	isFetching,
	columns
}: TableIsFetchingRowProps) => {
	if (!isFetching) {
		return null;
	}

	const isFetchingColumns = Array.from(
		{ length: columns },
		(_, index) => `fetching-column-${index}`
	);

	return (
		<TableRow data-testid="table-fetching-row">
			{isFetchingColumns.map(key => (
				<TableCell key={key}>
					<div className="inline-flex min-h-4 justify-center gap-3">
						<span className="animate-loader min-h-2 w-2 rounded-full bg-dm-black" />
						<span className="animation-delay-100 animate-loader min-h-2 w-2 rounded-full bg-dm-black" />
						<span className="animation-delay-200 animate-loader min-h-2 w-2 rounded-full bg-dm-black" />
					</div>
				</TableCell>
			))}
		</TableRow>
	);
};

type TableLoadingRowsProps = {
	columns: number;
};

export const TableLoadingRows = ({ columns }: TableLoadingRowsProps) => {
	const loadingRows = Array.from(
		{ length: 20 },
		(_, index) => `loading-row-${index}`
	);
	const loadingColumns = Array.from(
		{ length: columns },
		(_, index) => `loading-column-${index}`
	);

	return (
		<>
			{loadingRows.map(rowKey => (
				<TableRow key={rowKey} data-testid="table-loading-row">
					{loadingColumns.map(columnKey => (
						<TableCell key={columnKey}>
							<span className="inline-flex min-h-3 w-full animate-pulse rounded-md bg-dm-black" />
						</TableCell>
					))}
				</TableRow>
			))}
		</>
	);
};

TableBody.displayName = 'TableBody';

export default TableBody;
